import ProjectIntakeStatus from 'src/enums/companyProjects/ProjectIntakeStatus';
import {
  ProjectIntakeEventType,
  type ProjectIntakeEvent,
  type ProjectIntakeInboxModel,
  type ProjectIntakeSortByPropName
} from '../types';

export const sortInboxItems = (
  items: ProjectIntakeInboxModel[],
  sortBy?: ProjectIntakeSortByPropName,
  desc?: boolean
): ProjectIntakeInboxModel[] => {
  if (!sortBy) {
    return items;
  }

  const sorted = [...items].sort((a, b) => {
    switch (sortBy) {
      case 'from': {
        return a.From.localeCompare(b.From);
      }
      case 'subject': {
        return a.Subject.localeCompare(b.Subject);
      }
      case 'assignedtousername': {
        const aName = a.AssignedToUserName ?? '';
        const bName = b.AssignedToUserName ?? '';
        return aName.localeCompare(bName);
      }
      case 'initialemailreceiveddate': {
        const aDate = a.InitialEmailReceivedDate ? new Date(a.InitialEmailReceivedDate).getTime() : 0;
        const bDate = b.InitialEmailReceivedDate ? new Date(b.InitialEmailReceivedDate).getTime() : 0;
        return aDate - bDate;
      }
      default:
        return 0;
    }
  });

  if (desc) {
    sorted.reverse();
  }

  return sorted;
};

const getEventIntakeId = (event: ProjectIntakeEvent): number | undefined => {
  return event.data?.Id ?? event.intakeId;
};

const upsertInboxItem = (
  current: ProjectIntakeInboxModel[],
  id: number,
  patch: Partial<ProjectIntakeInboxModel>,
  prependIfNew: boolean
): ProjectIntakeInboxModel[] => {
  let found = false;

  const updated = current.map((item) => {
    if (item.Id !== id) {
      return item;
    }
    found = true;
    return {
      ...item,
      ...patch
    };
  });

  if (found) {
    return updated;
  }

  const newItem: ProjectIntakeInboxModel = {
    Id: id,
    ...patch
  } as ProjectIntakeInboxModel;

  return prependIfNew ? [newItem, ...updated] : [...updated, newItem];
};

const updateHasNewMessage = (
  current: ProjectIntakeInboxModel[],
  intakeId: number,
  hasNewMessage: boolean
): ProjectIntakeInboxModel[] => {
  let changed = false;

  const next = current.map((item) => {
    if (item.Id !== intakeId) {
      return item;
    }
    changed = true;
    if (item.HasNewMessage === hasNewMessage) {
      return item;
    }
    return {
      ...item,
      HasNewMessage: hasNewMessage
    };
  });

  return changed ? next : current;
};

export const applyInboxEventToList = (
  current: ProjectIntakeInboxModel[],
  event: ProjectIntakeEvent,
  sortBy?: ProjectIntakeSortByPropName,
  desc?: boolean
): ProjectIntakeInboxModel[] => {
  const intakeId = getEventIntakeId(event);
  if (!intakeId) {
    console.warn('ProjectIntake inbox event without intakeId or data.Id, skipping', event);
    return current;
  }

  let next = current;

  switch (event.type) {
    case ProjectIntakeEventType.Created: {
      const payload = event.data;
      if (!payload) {
        console.warn('ProjectIntake Created event without data payload, skipping', event);
        return current;
      }
      const targetId = payload.Id ?? intakeId;
      next = upsertInboxItem(current, targetId, payload, true);
      break;
    }
    case ProjectIntakeEventType.Removed: {
      next = current.filter((item) => item.Id !== intakeId);
      break;
    }
    case ProjectIntakeEventType.Read: {
      next = updateHasNewMessage(current, intakeId, false);
      if (next === current) {
        return current;
      }
      break;
    }
    case ProjectIntakeEventType.Unread: {
      next = updateHasNewMessage(current, intakeId, true);
      if (next === current) {
        return current;
      }
      break;
    }
    case ProjectIntakeEventType.Updated: {
      const payload = event.data;
      if (!payload) {
        console.warn('ProjectIntake Updated event without data payload, skipping', event);
        return current;
      }
      const targetId = payload.Id ?? intakeId;
      next = upsertInboxItem(current, targetId, payload, true);
      break;
    }
    case ProjectIntakeEventType.Merged: {
      next = current.filter((item) => item.Id !== intakeId);
      break;
    }
    default:
      return current;
  }

  return sortInboxItems(next, sortBy, desc);
};

const parseProjectIntakeEventType = (value: unknown): ProjectIntakeEventType | undefined => {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 5) {
    return value as ProjectIntakeEventType;
  }
  if (typeof value === 'string') {
    const byName: Record<string, ProjectIntakeEventType> = {
      Created: ProjectIntakeEventType.Created,
      Removed: ProjectIntakeEventType.Removed,
      Read: ProjectIntakeEventType.Read,
      Unread: ProjectIntakeEventType.Unread,
      Updated: ProjectIntakeEventType.Updated,
      Merged: ProjectIntakeEventType.Merged
    };
    return byName[value];
  }
  return undefined;
};

const parseProjectIntakeStatus = (value: unknown): ProjectIntakeStatus => {
  if (typeof value === 'number' && Number.isInteger(value)) {
    return value as ProjectIntakeStatus;
  }
  if (typeof value === 'string') {
    const byName: Record<string, ProjectIntakeStatus> = {
      New: ProjectIntakeStatus.New,
      InProgress: ProjectIntakeStatus.InProgress,
      AwaitingResponse: ProjectIntakeStatus.AwaitingResponse,
      Merged: ProjectIntakeStatus.Merged,
      Deleted: ProjectIntakeStatus.Deleted
    };
    return byName[value] ?? ProjectIntakeStatus.New;
  }
  return ProjectIntakeStatus.New;
};

/** Merge camelCase SignalR JSON with the PascalCase inbox model shape used in React Query. */
const normalizeInboxItemData = (rawData: unknown): ProjectIntakeInboxModel | null => {
  if (!rawData || typeof rawData !== 'object') return null;
  const d = rawData as Record<string, unknown>;
  const id = (d.Id ?? d.id) as number | undefined;
  if (id == null || typeof id !== 'number') return null;

  const created =
    (d.CreatedOn ?? d.createdOn) != null ? new Date(String(d.CreatedOn ?? d.createdOn)).toISOString() : undefined;
  const initialEmail =
    (d.InitialEmailReceivedDate ?? d.initialEmailReceivedDate) != null
      ? String(d.InitialEmailReceivedDate ?? d.initialEmailReceivedDate)
      : created;

  return {
    Id: id,
    From: String(d.From ?? d.from ?? ''),
    Subject: String(d.Subject ?? d.subject ?? ''),
    HasNewMessage: Boolean(d.HasNewMessage ?? d.hasNewMessage),
    AssignedToUserName: (d.AssignedToUserName ?? d.assignedToUserName) as string | undefined,
    AssignedToUserId: (d.AssignedToUserId ?? d.assignedToUserId) as string | undefined,
    Status: parseProjectIntakeStatus(d.Status ?? d.status),
    InitialEmailReceivedDate: initialEmail,
    LatestMessageDate:
      (d.LatestMessageDate ?? d.latestMessageDate) != null
        ? new Date(String(d.LatestMessageDate ?? d.latestMessageDate)).toISOString()
        : undefined,
    MessageCount: Number(d.MessageCount ?? d.messageCount ?? 0),
    UpdatedOn:
      (d.UpdatedOn ?? d.updatedOn) != null ? new Date(String(d.UpdatedOn ?? d.updatedOn)).toISOString() : undefined
  };
};

export const normalizeInboxEvent = (args: unknown[]): ProjectIntakeEvent | null => {
  if (!args.length) return null;
  const payload = args[0];

  if (!payload || typeof payload !== 'object') return null;

  const raw = payload as Record<string, unknown>;

  const type = parseProjectIntakeEventType(raw.type ?? raw.Type);
  if (type === undefined) {
    console.warn('[ProjectDashboardSignalR] Inbox event missing or invalid type', raw);
    return null;
  }

  const dataRaw = raw.data ?? raw.Data;
  const data = dataRaw != null ? (normalizeInboxItemData(dataRaw) ?? undefined) : undefined;

  const intakeIdRaw = raw.intakeId ?? raw.IntakeId ?? data?.Id;
  const intakeIdNum = typeof intakeIdRaw === 'number' ? intakeIdRaw : Number(intakeIdRaw);
  const intakeId = Number.isFinite(intakeIdNum) ? intakeIdNum : (data?.Id ?? 0);

  return {
    intakeId,
    type,
    data
  };
};
